import { keyBy } from 'lodash';
import moment from 'moment';
import { concat, map, pipe, uniqBy } from 'ramda';
import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Team, User } from 'api/models';
import { PlanningLevelConfig } from 'api/resource-management/types';
import { PageLoading } from 'components/atoms/loading';
import { Page } from 'components/molecules/page';
import { RootState } from 'data';
import {
  useActiveProjectTemplate,
  useGetAllTasks,
} from 'data/templates/projects';
import { getActiveProjectTemplate } from 'data/templates/projects/sagas/save';
import { getActiveTemplatePlaceholders } from 'data/templates/projects/selectors';
import { useNativeActiveOrInvitedUsersQuery } from 'queries/members';
import { useDefaultNativeTeamsInAccount } from 'queries/placeholder';
import { useResourceManagementConfigQuery } from 'queries/resource-management-configuration';
import { useVendorRolesQuery } from 'queries/roles';
import { useUserFieldsAsFilterableFields } from 'utils/user-fields';
import { TemplateDetailHeader } from '../../commons';
import { ResourceManagementContent } from './content';
import { ResourceManagementFeatureDiscovery } from './feature-discovery';
import { asProject, asProjectWithMember } from './utils/as-project';
import { getAllAssignees } from './utils/assignee-data';

const Content = styled(Page.Content)`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 188px);
  overflow: auto;
`;

export const TemplateResourceManagement: FC = () => {
  const { data: resourceManagementConfig } = useResourceManagementConfigQuery();

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  const { data: roles, isLoading: isRolesLoading } = useVendorRolesQuery();
  const { data: nativeUsers, isLoading: isUsersLoading } =
    useNativeActiveOrInvitedUsersQuery();
  const userLookup = useMemo(
    () => keyBy(nativeUsers ?? [], 'userId'),
    [nativeUsers],
  );
  const templatePlaceholders = useSelector(getActiveTemplatePlaceholders());
  const allTasks = useGetAllTasks();
  const { data: defaultTeams, isLoading: isTeamsLoading } =
    useDefaultNativeTeamsInAccount();
  const allTeams = useMemo(
    () =>
      pipe(
        () => Object.values(templatePlaceholders),
        concat(defaultTeams ?? []),
        uniqBy((x) => x.teamId),
        map((x) => ({ ...x, resolved: false })),
      )(),
    [defaultTeams, templatePlaceholders],
  );
  const relativeDate = useMemo(
    () => moment().startOf(isHoursPerWeek ? 'week' : 'day'),
    [isHoursPerWeek],
  );
  const teamLookup = useMemo(() => keyBy(allTeams, 'teamId'), [allTeams]);
  const { isFieldsLoading: isUserFieldsLoading } =
    useUserFieldsAsFilterableFields();
  const activeProjectTemplate = getActiveProjectTemplate(
    useSelector((state: RootState) => state),
  );

  const {
    templateContent: { autoAllocation: autoAllocate = false },
  } = useActiveProjectTemplate();

  const allTasksAssignees = useMemo(
    () => getAllAssignees(Object.values(allTasks)),
    [allTasks],
  );
  const projectFromTemplate = useMemo(() => {
    return asProjectWithMember(
      asProject(
        activeProjectTemplate,
        uniqBy((x: User) => x.userId)(
          activeProjectTemplate.allocatedMembers.users.concat(
            allTasksAssignees.users,
          ),
        ),
        relativeDate,
      ),
    );
  }, [activeProjectTemplate, allTasksAssignees.users, relativeDate]);
  const teamMembership = useMemo(() => {
    return {
      [projectFromTemplate.projectId]: uniqBy((x: Team) => x.teamId)(
        activeProjectTemplate.allocatedMembers.teams
          .concat(allTasksAssignees.teams)
          .map((team) => ({ ...team, resolved: false })),
      ),
    };
  }, [
    activeProjectTemplate.allocatedMembers.teams,
    allTasksAssignees.teams,
    projectFromTemplate.projectId,
  ]);
  const projectLookup = useMemo(
    () => ({
      [projectFromTemplate.projectId]: projectFromTemplate,
    }),
    [projectFromTemplate],
  );

  const hasResourceManagementDataForTemplate = useMemo(
    () =>
      projectFromTemplate.projectMembers.length > 0 ||
      teamMembership[projectFromTemplate.projectId].length > 0 ||
      autoAllocate,
    [
      autoAllocate,
      projectFromTemplate.projectId,
      projectFromTemplate.projectMembers.length,
      teamMembership,
    ],
  );
  const [forceVisibleContent, setForceVisibleContent] = useState(
    hasResourceManagementDataForTemplate,
  );

  return (
    <Page>
      <TemplateDetailHeader />
      <Content>
        {isRolesLoading ||
        isUsersLoading ||
        isTeamsLoading ||
        isUserFieldsLoading ? (
          <PageLoading css />
        ) : forceVisibleContent || hasResourceManagementDataForTemplate ? (
          <ResourceManagementContent
            userLookup={userLookup}
            projectLookup={projectLookup}
            teamLookup={teamLookup}
            roles={roles ?? []}
            relativeDate={relativeDate}
            project={projectFromTemplate}
            teamMembership={teamMembership}
            allocatedMembers={activeProjectTemplate.allocatedMembers}
            allTasksAssignees={allTasksAssignees}
            autoAllocate={autoAllocate}
          />
        ) : (
          <ResourceManagementFeatureDiscovery
            onGetStarted={() => setForceVisibleContent(true)}
          />
        )}
      </Content>
    </Page>
  );
};
